/**
 * Модуль демо: попап записи на демонстрацию (нативный аналог Popup.vue).
 */
import config from '@appConfig';
import DemoApi from 'keysso/api/DemoApi';
import { DaData } from 'keysso/services/DaData';

const LISTENER_OPEN = 'popup-demo-block-open';
const MIN_INN_LENGTH = 3;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TEXT = {
    incorrectEmail: 'Некорректный email',
    captchaUnavailable:
        'Проверка безопасности недоступна из вашего региона — попробуйте VPN или другой браузер',
    toastTitle: 'Заявка на демонстрацию отправлена',
    toastDesc:
        'Мы свяжемся с вами при первой возможности по e-mail или в мессенджере для уточнения деталей.\n\nНаш график работы — с 10:00 до 17:00 (мск) по будням.',
    verboxTemplate:
        '***Заявка на демонстрацию отправлена***\n\nИмя: {name}\nEmail: {email}\nТелефон: {phone}\nДолжность: {job}\nСайт вашей компании: {site}\nКомпания или ИНН: {inn}\nВопрос до демонстрации: {question}\n\nСвяжемся с вами в чате поддержки, по e-mail или в мессенджере, для согласования дня и времени демонстрации. Наш график работы — с 10:00 до 17:00 (мск) по будням.',
};

/** Лёгкий emitter, если Vue-бандл не подключён */
function ensureEmitter() {
    if (window.keysoEmitter) {
        return;
    }

    const listeners = Object.create(null);

    window.keysoEmitter = {
        on(event, handler) {
            if (!listeners[event]) {
                listeners[event] = [];
            }
            listeners[event].push(handler);
        },
        emit(event, data) {
            (listeners[event] || []).forEach(function (handler) {
                handler(data);
            });
        },
    };
}

const CAPTCHA_SCRIPT_URL = 'https://smartcaptcha.cloud.yandex.ru/captcha.js?render=onload';
const CAPTCHA_SCRIPT_MARKER = 'data-smartcaptcha';
// ожидание загрузки скрипта капчи: 50 попыток по 100мс
const CAPTCHA_READY_ATTEMPTS = 50;
const CAPTCHA_READY_DELAY = 100;
// 428: заявка требует капчу, запрос нужно повторить с токеном
const CAPTCHA_REQUIRED_CODE = 428;

function isCaptchaActive() {
    return process.env.APP_ENV === 'prod' && !config.CAPTCHA_DISABLED;
}

function formatPhone(raw) {
    const digits = String(raw || '').replace(/\D/g, '').slice(0, 11);
    if (!digits.length) {
        return '';
    }

    let result = '+' + digits[0];
    if (digits.length > 1) {
        result += ' ' + digits.slice(1, 4);
    }
    if (digits.length > 4) {
        result += ' ' + digits.slice(4, 7);
    }
    if (digits.length > 7) {
        result += '-' + digits.slice(7, 9);
    }
    if (digits.length > 9) {
        result += '-' + digits.slice(9, 11);
    }

    return result;
}

function interpolate(template, values) {
    return template.replace(/\{(\w+)\}/g, function (_, key) {
        return values[key] != null ? values[key] : '';
    });
}

function showToast(title, description) {
    let toast = document.querySelector('.demo-popup__toast');
    if (!toast) {
        toast = document.createElement('div');
        toast.className = 'demo-popup__toast';
        toast.setAttribute('role', 'status');
        document.body.appendChild(toast);
    }

    toast.innerHTML =
        '<strong class="demo-popup__toast-title"></strong>' +
        '<p class="demo-popup__toast-desc"></p>' +
        '<button type="button" class="demo-popup__toast-close" aria-label="Закрыть">&times;</button>';

    toast.querySelector('.demo-popup__toast-title').textContent = title;
    toast.querySelector('.demo-popup__toast-desc').textContent = description;
    toast.classList.add('is-visible');

    const close = function () {
        toast.classList.remove('is-visible');
    };

    toast.querySelector('.demo-popup__toast-close').addEventListener('click', close, { once: true });
}

/**
 * Инициализация попапа демо.
 */
function initDemoPopup() {
    const root = document.getElementById('demo-popup');
    if (!root || root.dataset.demoPopupReady === '1') {
        return;
    }
    root.dataset.demoPopupReady = '1';

    const dialog = root.querySelector('.demo-popup__dialog');
    const form = root.querySelector('.demo-popup__form');
    const submitBtn = root.querySelector('.demo-popup__submit');
    const captchaContainer = root.querySelector('.demo-popup__captcha');
    const errorBox = root.querySelector('.demo-popup__error');
    const loader = root.querySelector('.demo-popup__loader');
    const agreeInput = root.querySelector('#demo-popup-privacy');
    const emailInput = form.querySelector('[name="email"]');
    const emailError = root.querySelector('.demo-popup__field-error--email');
    const innInput = form.querySelector('[name="inn"]');
    const innList = root.querySelector('.demo-popup__suggest');
    const fields = ['name', 'email', 'phone', 'job', 'site', 'inn', 'question'];

    let loading = false;
    let emailValid = false;
    let captchaRequired = false;
    let captchaToken = '';
    let captchaSentToken = '';
    let captchaWidgetId = null;
    let suggestTimer = null;
    let suggestItems = [];
    let selectedInn = '';

    const defaultEmail = root.getAttribute('data-email') || '';
    const defaultPhone = root.getAttribute('data-phone') || '';
    const isElama = root.getAttribute('data-elama') === '1';

    function setLoading(value) {
        loading = value;
        root.classList.toggle('is-loading', value);
        if (loader) {
            loader.hidden = !value;
        }
        updateSubmitState();
    }

    function setError(message) {
        if (!errorBox) {
            return;
        }
        if (message) {
            errorBox.textContent = message;
            errorBox.hidden = false;
        } else {
            errorBox.textContent = '';
            errorBox.hidden = true;
        }
    }

    function getModel() {
        const model = {};
        fields.forEach(function (name) {
            const input = form.querySelector('[name="' + name + '"]');
            model[name] = input ? String(input.value || '').trim() : '';
        });
        return model;
    }

    function clearModel() {
        fields.forEach(function (name) {
            const input = form.querySelector('[name="' + name + '"]');
            if (!input) {
                return;
            }
            if (name === 'email') {
                input.value = defaultEmail;
            } else if (name === 'phone') {
                input.value = defaultPhone ? formatPhone(defaultPhone) : '';
            } else {
                input.value = '';
            }
        });
        agreeInput.checked = true;
        selectedInn = '';
        emailValid = false;
        hideSuggest();
        setError('');
        validateEmail();
        updateSubmitState();
    }

    function validateEmail() {
        const value = String(emailInput.value || '').trim();
        if (!value.length) {
            emailValid = false;
            emailInput.classList.remove('is-invalid');
            if (emailError) {
                emailError.hidden = true;
            }
            return;
        }

        emailValid = EMAIL_RE.test(value);
        emailInput.classList.toggle('is-invalid', !emailValid);
        if (emailError) {
            emailError.hidden = emailValid;
        }
    }

    function isDisableSend() {
        const model = getModel();
        return (
            loading ||
            !emailValid ||
            !model.name ||
            !model.email ||
            !model.phone ||
            !model.job ||
            !model.site ||
            !model.inn ||
            !agreeInput.checked
        );
    }

    function updateSubmitState() {
        submitBtn.disabled = isDisableSend();
    }

    function openPopup(extra) {
        clearModel();
        if (extra && typeof extra === 'object') {
            Object.keys(extra).forEach(function (key) {
                const input = form.querySelector('[name="' + key + '"]');
                if (input && extra[key] != null) {
                    input.value = key === 'phone' ? formatPhone(extra[key]) : extra[key];
                }
            });
            validateEmail();
            updateSubmitState();
        }

        root.hidden = false;
        root.setAttribute('aria-hidden', 'false');
        document.body.classList.add('demo-popup-open');
        initCaptcha();

        const focusTarget = form.querySelector('[name="name"]');
        if (focusTarget) {
            setTimeout(function () {
                focusTarget.focus();
            }, 50);
        }
    }

    function closePopup() {
        root.hidden = true;
        root.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('demo-popup-open');
        hideSuggest();
        setLoading(false);
        setError('');
        resetCaptcha();
    }

    function loadCaptchaScript() {
        if (document.querySelector('script[' + CAPTCHA_SCRIPT_MARKER + ']')) {
            return;
        }

        const script = document.createElement('script');
        script.async = true;
        script.src = CAPTCHA_SCRIPT_URL;
        script.setAttribute(CAPTCHA_SCRIPT_MARKER, '1');
        document.head.appendChild(script);
    }

    function whenCaptchaReady(callback, attempt) {
        if (typeof window.smartCaptcha !== 'undefined' && typeof window.smartCaptcha.render === 'function') {
            callback();
            return;
        }

        if (attempt >= CAPTCHA_READY_ATTEMPTS) {
            return;
        }

        setTimeout(function () {
            whenCaptchaReady(callback, attempt + 1);
        }, CAPTCHA_READY_DELAY);
    }

    function initCaptcha() {
        if (!isCaptchaActive() || !captchaContainer) {
            return;
        }

        loadCaptchaScript();
        whenCaptchaReady(function () {
            if (captchaWidgetId !== null) {
                window.smartCaptcha.reset(captchaWidgetId);
                return;
            }

            captchaWidgetId = window.smartCaptcha.render(captchaContainer, {
                sitekey: config.CAPTACHA_V2_KEY,
                invisible: true,
                callback: onCaptcha,
            });
        }, 0);
    }

    function executeCaptcha() {
        if (captchaWidgetId === null) {
            // виджет не загрузился: капча недоступна в сети пользователя
            setError(TEXT.captchaUnavailable);
            return;
        }

        window.smartCaptcha.execute(captchaWidgetId);
    }

    function onCaptcha(token) {
        // каждый execute() вызывает callback, поэтому при двойном клике токен приходит дважды.
        // Токен одноразовый: вторую отправку бэкенд отклонит как "token has already been used"
        if (!token || token === captchaSentToken) {
            return;
        }

        captchaSentToken = token;
        captchaToken = token;
        onSend();
    }

    function resetCaptcha() {
        captchaToken = '';
        if (captchaWidgetId !== null) {
            // токен одноразовый, без сброса повторная отправка не пройдёт проверку
            window.smartCaptcha.reset(captchaWidgetId);
        }
    }

    function onAgree() {
        // капча нужна только после нескольких заявок: первые уходят без неё
        if (isCaptchaActive() && captchaRequired) {
            // отправку запускает callback капчи
            executeCaptcha();
            return;
        }
        onSend();
    }

    function onSend() {
        if (isDisableSend()) {
            return;
        }

        const model = getModel();
        setLoading(true);
        setError('');

        const withCaptcha = isCaptchaActive() && captchaRequired;
        const payload = Object.assign({}, model);
        if (withCaptcha) {
            payload.captchaToken = captchaToken;
        }

        DemoApi.createDemoBid(payload)
            .then(function () {
                const verboxMessage = interpolate(TEXT.verboxTemplate, model);

                clearModel();
                closePopup();
                showToast(TEXT.toastTitle, TEXT.toastDesc);

                if (isElama) {
                    return;
                }

                if (typeof Verbox === 'function') {
                    Verbox('sendMessage', {
                        text: verboxMessage,
                    });
                }
            })
            .catch(function (error) {
                resetCaptcha();

                // Нужна ли капча, решает только бэкенд: на 428 повторяем отправку с токеном.
                // Повтор ровно один — на нём withCaptcha уже true
                if (error && error.status === CAPTCHA_REQUIRED_CODE && isCaptchaActive() && !withCaptcha) {
                    captchaRequired = true;
                    onAgree();
                    return;
                }

                const message =
                    (error && error.message) ||
                    (typeof error === 'string' ? error : null) ||
                    'Не удалось отправить заявку';
                setError(message);
            })
            .finally(function () {
                setLoading(false);
            });
    }

    function hideSuggest() {
        if (!innList) {
            return;
        }
        innList.hidden = true;
        innList.innerHTML = '';
        suggestItems = [];
    }

    function renderSuggest(items) {
        suggestItems = items || [];
        if (!innList) {
            return;
        }

        if (!suggestItems.length) {
            hideSuggest();
            return;
        }

        innList.innerHTML = '';
        suggestItems.forEach(function (item, index) {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'demo-popup__suggest-item';
            button.dataset.index = String(index);

            const title = document.createElement('span');
            title.className = 'demo-popup__suggest-title';
            title.textContent = item.value || '';

            const meta = document.createElement('span');
            meta.className = 'demo-popup__suggest-meta';
            const country = item.data && item.data.address && item.data.address.data
                ? item.data.address.data.country
                : '';
            const address = item.data && item.data.address ? item.data.address.value : '';
            meta.textContent =
                'ИНН: ' +
                (item.data && item.data.inn ? item.data.inn : '') +
                (country ? ', ' + country : '') +
                (address ? ', ' + address : '');

            button.appendChild(title);
            button.appendChild(meta);
            innList.appendChild(button);
        });

        innList.hidden = false;
    }

    function selectSuggest(index) {
        const item = suggestItems[index];
        if (!item) {
            return;
        }

        const value =
            (item.data && item.data.inn ? item.data.inn : '') +
            ', ' +
            (item.value || '');
        innInput.value = value;
        selectedInn = value;
        hideSuggest();
        updateSubmitState();
    }

    async function fetchSuggest(query) {
        if (!query || query.length < MIN_INN_LENGTH) {
            hideSuggest();
            return;
        }

        const items = await DaData.getSuggestions(query, function (error) {
            const message =
                (error && error.message) ||
                (typeof error === 'string' ? error : null) ||
                'Ошибка подсказок DaData';
            setError(message);
        });
        renderSuggest(items);
    }

    // Закрытие
    root.querySelectorAll('[data-demo-close]').forEach(function (el) {
        el.addEventListener('click', function (event) {
            event.preventDefault();
            closePopup();
        });
    });

    document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && !root.hidden) {
            closePopup();
        }
    });

    // Поля формы
    form.addEventListener('input', function (event) {
        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }

        if (target.getAttribute('name') === 'phone') {
            const caretEnd = target.selectionEnd;
            const before = target.value;
            target.value = formatPhone(target.value);
            if (document.activeElement === target && typeof caretEnd === 'number') {
                const delta = target.value.length - before.length;
                const pos = Math.max(0, caretEnd + delta);
                target.setSelectionRange(pos, pos);
            }
        }

        if (target.getAttribute('name') === 'email') {
            validateEmail();
        }

        if (target.getAttribute('name') === 'inn') {
            const query = String(target.value || '').trim();
            if (selectedInn && query !== selectedInn) {
                selectedInn = '';
            }
            clearTimeout(suggestTimer);
            suggestTimer = setTimeout(function () {
                fetchSuggest(query);
            }, 250);
        }

        updateSubmitState();
    });

    agreeInput.addEventListener('change', updateSubmitState);

    innList.addEventListener('mousedown', function (event) {
        // mousedown до blur: выбор из списка не сбрасывает поле
        event.preventDefault();
        const item = event.target.closest('.demo-popup__suggest-item');
        if (!item) {
            return;
        }
        selectSuggest(Number(item.dataset.index));
    });

    innInput.addEventListener('blur', function () {
        setTimeout(function () {
            hideSuggest();
            // forceSelection: значение только из подсказки DaData
            if (innInput.value && innInput.value !== selectedInn) {
                innInput.value = '';
                selectedInn = '';
                updateSubmitState();
            }
        }, 150);
    });

    document.addEventListener('click', function (event) {
        if (innList.hidden) {
            return;
        }
        if (event.target === innInput || innList.contains(event.target)) {
            return;
        }
        hideSuggest();
    });

    submitBtn.addEventListener('click', function (event) {
        event.preventDefault();
        onAgree();
    });

    form.addEventListener('submit', function (event) {
        event.preventDefault();
        onAgree();
    });

    // Кнопки «Записаться на демо» на странице
    document.querySelectorAll('.demo__btn').forEach(function (button) {
        if (button.dataset.demoReady === '1') {
            return;
        }
        button.dataset.demoReady = '1';
        button.addEventListener('click', function (event) {
            event.preventDefault();
            if (window.keysoEmitter) {
                window.keysoEmitter.emit(LISTENER_OPEN);
            } else {
                openPopup();
            }
        });
    });

    ensureEmitter();
    window.keysoEmitter.on(LISTENER_OPEN, function (data) {
        openPopup(data || {});
    });

    clearModel();
}

ensureEmitter();

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDemoPopup);
} else {
    initDemoPopup();
}
